import React, { useState, useEffect, useRef } from "react";
import { useTheme } from "@/contexts/ThemeContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useUser } from "@/contexts/UserContextProvider";
import { BeautifulTabsSelector } from "@/components/ui/BeautifulTabsSelector";
import {
  getAuth,
  EmailAuthProvider,
  reauthenticateWithCredential,
  RecaptchaVerifier,
  ApplicationVerifier,
  PhoneAuthProvider,
  MultiFactorResolver,
  GoogleAuthProvider,
  OAuthProvider,
  linkWithPopup,
  unlink,
  UserInfo
} from "firebase/auth";
import {
  updatePassword,
  verifyPhoneNumber,
  unenroll2FA,
  enroll2FA,
  getEnrolledFactors,
  MultiFactorInfo
} from "@/services/authService";
import { getUserInfo } from '@/services/userService';
import { getAuthUID } from '@/lib/authState';
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogAction, AlertDialogCancel } from "@/components/ui/alert-dialog";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock } from "@awesome.me/kit-269fbef2e2/icons/duotone/solid";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { renderDate } from "../../../../../shared/renderDate";

// Define TwoFactorMethod type to handle MFA data
type TwoFactorMethod = {
  uid: string;
  phoneNumber: string;
  displayName: string;
  enrollmentTime: string;
};

// Helper function to convert MultiFactorInfo to TwoFactorMethod
const mapToTwoFactorMethod = (factor: MultiFactorInfo): TwoFactorMethod => {
  // For phone factors, the phone number is in the factor object
  // For other factors, we might need to handle them differently
  const phoneNumber = 'phoneNumber' in factor ? factor.phoneNumber :
                    'factorId' in factor && factor.factorId === 'phone' ?
                    (factor as any).phoneNumber : '';

  return {
    uid: factor.uid,
    phoneNumber: phoneNumber || 'Unknown',
    displayName: factor.displayName || 'Phone',
    enrollmentTime: factor.enrollmentTime ? renderDate(factor.enrollmentTime) : 'Unknown date'
  };
};

export default function SecuritySection() {
  const { isDark } = useTheme();
  const { user } = useUser();

  // Password state
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);
  const [selectedTab, setSelectedTab] = useState("password");

  // 2FA state
  const [countryCode, setCountryCode] = useState("+61");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [showVerificationCode, setShowVerificationCode] = useState(false);
  const [isSubmittingCode, setIsSubmittingCode] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [pendingPhoneNumber, setPendingPhoneNumber] = useState("");
  const [mfaCode, setMfaCode] = useState('');
  const [isVerifyingMfa, setIsVerifyingMfa] = useState(false);
  const [showMfaVerification, setShowMfaVerification] = useState(false);
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [pendingUnenrollFactor, setPendingUnenrollFactor] = useState<TwoFactorMethod | null>(null);
  const [selectedFactor, setSelectedFactor] = useState<TwoFactorMethod | null>(null);
  const [verificationSent, setVerificationSent] = useState(false);
  
  // Verification code expiry timer state - typical Firebase SMS codes expire after 2 minutes
  const [expiryTime, setExpiryTime] = useState<Date | null>(null);
  const [timeRemaining, setTimeRemaining] = useState<number>(0);
  
  // For enrolled MFA methods
  const [enrolledMethods, setEnrolledMethods] = useState<TwoFactorMethod[]>([]);
  
  // For managing factor selection in unenroll flow
  const [selectedFactorUid, setSelectedFactorUid] = useState<string | null>(null);
  const [showUnenrollDialog, setShowUnenrollDialog] = useState(false);

  // Password verification state
  const [showPasswordPrompt, setShowPasswordPrompt] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  
  // SSO state
  const [linkedProviders, setLinkedProviders] = useState<string[]>([]);
  const [isLinkingProvider, setIsLinkingProvider] = useState<string | null>(null);
  const [isUnlinkingProvider, setIsUnlinkingProvider] = useState<string | null>(null);
  
  // Define a type for pending actions that might need verification code and ID
  type PendingActionType = ((verificationCode?: string, verificationId?: string) => Promise<boolean | void>) | null;
  const [pendingAction, setPendingAction] = useState<PendingActionType>(null);
  const [isVerifyingPassword, setIsVerifyingPassword] = useState(false);

  // Function to refresh user data
  const refreshUser = async () => {
    try {
      const updatedUser = await getUserInfo();
      if (updatedUser) {
        // Update any component state that depends on user data
        // For example, if you have a user state:
        // setUser(updatedUser);
        console.log('User data refreshed:', updatedUser);
      }
      return updatedUser;
    } catch (error) {
      console.error('Error refreshing user data:', error);
      return null;
    }
  };

  // Function to refresh enrolled factors
  const refreshEnrolledFactors = async () => {
    try {
      const factors = await getEnrolledFactors();
      setEnrolledMethods(
        factors.map((factor) => ({
          uid: factor.uid,
          phoneNumber: factor.phoneNumber || '',
          displayName: factor.displayName || 'Phone',
          enrollmentTime: factor.enrollmentTime ? renderDate(factor.enrollmentTime) : 'Unknown date',
          factorId: factor.factorId
        }))
      );
    } catch (error) {
      console.error('Error loading enrolled factors:', error);
      toast.error('Failed to load 2FA methods');
    }
  };

  // Load enrolled 2FA methods and linked providers
  useEffect(() => {
    let isMounted = true;

    const loadEnrolledFactors = async () => {
      if (isMounted) {
        await refreshEnrolledFactors();
      }
    };

    const loadLinkedProviders = () => {
      const currentUser = getAuth().currentUser;
      if (currentUser && isMounted) {
        const providers = currentUser.providerData
          .map((provider: UserInfo) => provider.providerId)
          .filter((id: string) => id === 'google.com' || id === 'microsoft.com');
        setLinkedProviders(providers);
      }
    };

    // Use an async IIFE to call the async load function
    (async () => {
      await loadEnrolledFactors();
      loadLinkedProviders();
    })();

    // Cleanup function (only on component unmount)
    return () => {
      isMounted = false;
      // Only clean up reCAPTCHA verifier and DOM container on unmount
      if (window.recaptchaVerifier) {
        try {
          // Type assertion to access the clear method if it exists
          const verifier = window.recaptchaVerifier as ApplicationVerifier & { clear?: () => void };
          if (verifier.clear) {
            verifier.clear();
          }
        } catch (error) {
          console.warn('Error cleaning up reCAPTCHA verifier:', error);
        }
        delete window.recaptchaVerifier;
      }
      // Optionally clean up the DOM container
      const container = document.getElementById('recaptcha-container');
      if (container) {
        container.remove();
      }
      // Clear any stored verification IDs
      if (window.verificationId) {
        delete window.verificationId;
      }
    };
  }, [user]);
  
  // Timer effect for verification code expiry
  useEffect(() => {
    // Only run timer if we have an expiry time set
    if (!expiryTime) {
      setTimeRemaining(0);
      return;
    }
    
    // Calculate initial time remaining
    const calculateTimeRemaining = () => {
      const now = new Date();
      const diffMs = expiryTime.getTime() - now.getTime();
      const diffSec = Math.floor(diffMs / 1000);
      return diffSec > 0 ? diffSec : 0;
    };
    
    setTimeRemaining(calculateTimeRemaining());
    
    // Set up interval to update timer every second
    const timerInterval = setInterval(() => {
      const remaining = calculateTimeRemaining();
      setTimeRemaining(remaining);
      
      // If timer reached zero, clear everything
      if (remaining <= 0) {
        // Clear verification ID to prevent submitting an expired code
        if (window.verificationId) {
          console.log('Verification code expired, clearing ID');
          delete window.verificationId;
        }
        
        // Notify user
        toast.warning("Verification code has expired. Please request a new one.");
        
        // Clear the interval
        clearInterval(timerInterval);
      }
    }, 1000);
    
    // Cleanup on unmount
    return () => clearInterval(timerInterval);
  }, [expiryTime]);

  const handlePasswordUpdate = async (e: React.FormEvent) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    if (password.length < 8) {
      toast.error("Password must be at least 8 characters long");
      return;
    }

    // Set up the password change action
    const changePassword = async () => {
      try {
        setIsUpdatingPassword(true);
        await updatePassword(password);

        toast.success("Password updated successfully");

        setPassword("");
        setConfirmPassword("");
        setShowPasswordPrompt(false);
      } catch (error: any) {
        console.error("Error updating password:", error);
        throw error; // Re-throw to be handled by the dialog
      } finally {
        setIsUpdatingPassword(false);
      }
    };

    // Store the action and show password prompt
    setPendingAction(() => changePassword);
    setShowPasswordPrompt(true);
  };

  const handleVerifyPhoneNumber = async () => {
    if (!phoneNumber) {
      toast.error("Please enter a phone number");
      return;
    }

    // Ensure phone number is in E.164 format: +[country code][number] with no spaces or special chars
    const cleanedNumber = phoneNumber.replace(/[^\d+]/g, '');
    const fullPhoneNumber = cleanedNumber.startsWith('+')
      ? cleanedNumber
      : `${countryCode}${cleanedNumber}`;

    // Basic validation for phone number format
    if (!/^\+[1-9]\d{1,14}$/.test(fullPhoneNumber)) {
      toast.error("Please enter a valid phone number with country code");
      return;
    }

    try {
      setIsVerifying(true);

      try {
        // Ensure the reCAPTCHA container exists
        let container = document.getElementById('recaptcha-container');
        if (!container) {
          container = document.createElement('div');
          container.id = 'recaptcha-container';
          container.style.display = 'none';
          document.body.appendChild(container);
        } else {
          // Clear any existing content
          container.innerHTML = '';
        }

        // Create a new reCAPTCHA verifier instance with proper typing
        const auth = getAuth();
        const verifier = new RecaptchaVerifier(
          auth,
          'recaptcha-container',
          {
            size: 'invisible',
            callback: (response: string) => {
              console.log('reCAPTCHA verified:', response);
            },
            'expired-callback': () => {
              console.warn('reCAPTCHA expired');
              toast.warning('Verification expired. Please try again.');
            },
            'error-callback': (error: Error) => {
              console.error('reCAPTCHA error:', error);
              toast.error('Verification error. Please try again.');
            }
          }
        ) as unknown as { render: () => Promise<void> } & ApplicationVerifier;

        // Store the verifier in window for later use
        window.recaptchaVerifier = verifier as unknown as ApplicationVerifier;

        // Render the reCAPTCHA widget
        await verifier.render();

        console.log('reCAPTCHA verifier initialized successfully');
      } catch (error) {
        console.error('Error initializing reCAPTCHA verifier:', error);
        throw new Error('Failed to initialize security verification. Please refresh the page and try again.');
      }

      // Send verification code
      console.log('Sending verification code to:', fullPhoneNumber);
      const verificationId = await verifyPhoneNumber(fullPhoneNumber);

      if (!verificationId) {
        throw new Error('Failed to send verification code. Please try again.');
      }

      // Store the verification ID for later use
      window.verificationId = verificationId;
      console.log('Verification ID received:', verificationId);

      setShowVerificationCode(true);
      setPendingPhoneNumber(fullPhoneNumber);
      
      // Set expiry time for verification code (Firebase SMS codes typically expire after 2 minutes)
      const expiry = new Date();
      expiry.setMinutes(expiry.getMinutes() + 2); // 2 minute expiry
      setExpiryTime(expiry);
      setTimeRemaining(120); // 120 seconds

      toast.success("Verification code sent to your phone");
    } catch (error: any) {
      console.error("Error sending verification code:", error);

      // Handle specific error cases
      if (error.code === 'auth/requires-recent-login') {
        setPendingAction(() => handleVerifyPhoneNumber);
        setShowPasswordPrompt(true);
      } else {
        toast.error(error.message || "Failed to send verification code");
      }
    } finally {
      setIsVerifying(false);
    }
  };

  // Track last refreshVerification time to prevent multiple rapid calls
  const refreshVerificationDebounceRef = useRef<number>(0);
  const REFRESH_DEBOUNCE_MS = 1500; // 1.5 seconds debounce time
  
  const refreshVerification = async () => {
    // Implement debounce to prevent rapid consecutive calls
    const now = Date.now();
    if (now - refreshVerificationDebounceRef.current < REFRESH_DEBOUNCE_MS) {
      console.log('Debouncing refreshVerification - called too soon');
      toast.info('Please wait a moment before requesting another code');
      return;
    }
    refreshVerificationDebounceRef.current = now;
    // Reset the verification flow
    console.log('Refreshing verification flow');
    
    // Clear any existing verification ID
    if (window.verificationId) {
      delete window.verificationId;
    }
    
    // Clear any existing MFA session
    if ((window as any).mfaSession) {
      delete (window as any).mfaSession;
    }
    
    // Reset reCAPTCHA verifier - properly clear and remove
    if (window.recaptchaVerifier) {
      try {
        console.log('Resetting reCAPTCHA verifier');
        
        // Properly clear the reCAPTCHA using its clear method
        const verifier = window.recaptchaVerifier as ApplicationVerifier & { clear?: () => void };
        if (verifier.clear) {
          verifier.clear();
        }
        
        // Remove the entire reCAPTCHA container completely (not just clearing innerHTML)
        const container = document.getElementById('recaptcha-container');
        if (container) {
          container.remove();
        }
        
        // Create a new container with the same ID
        const newContainer = document.createElement('div');
        newContainer.id = 'recaptcha-container';
        document.body.appendChild(newContainer);
        
        // Clear the reference
        delete window.recaptchaVerifier;
      } catch (e) {
        console.error('Error clearing reCAPTCHA:', e);
      }
    }
    
    // Also clear the mfaUser to ensure a fresh session is obtained
    if (window.mfaUser) {
      console.log('Clearing mfaUser for fresh session');
      delete window.mfaUser;
    }
    
    // If we have a pending phone number, send a new verification code
    if (pendingPhoneNumber) {
      try {
        console.log('Re-sending verification to:', pendingPhoneNumber);
        setIsVerifying(true);
        const verificationId = await verifyPhoneNumber(pendingPhoneNumber);
        if (verificationId) {
          window.verificationId = verificationId;
          setVerificationSent(true);
          setVerificationCode(''); // Clear any previously entered code
          
          // DED-1520: Reset the expiry timer when a new code is sent
          const expiry = new Date();
          expiry.setMinutes(expiry.getMinutes() + 2); // 2 minute expiry
          setExpiryTime(expiry);
          setTimeRemaining(120); // 120 seconds
          
          toast.success(`New verification code sent to ${pendingPhoneNumber}`);
          return true;
        }
      } catch (error) {
        console.error('Error re-sending verification:', error);
        toast.error('Failed to send a new verification code. Please try again.');
      } finally {
        setIsVerifying(false);
      }
    }
    
    return false;
  };

  const handleVerifyCode = async () => {
    console.log('Verifying code:', verificationCode);
    if (!verificationCode) {
      toast.error("Please enter the verification code");
      return;
    }

    try {
      setIsSubmittingCode(true);

      if (!window.verificationId) {
        throw new Error('Verification ID not found. Please try again.');
      }

      console.log(`Attempting to verify code with verification ID: ${window.verificationId.substring(0, 5)}...`);
      
      // Enroll the 2FA method with the verification code
      await enroll2FA(pendingPhoneNumber, window.verificationId, verificationCode);

      // Handle successful 2FA enrollment
      const handle2FAEnrolled = async () => {
        try {
          // Refresh the list of enrolled factors
          await refreshEnrolledFactors();
          
          // Reset all relevant state
          setShowVerificationCode(false);
          setVerificationCode('');
          setPhoneNumber('');
          setVerificationSent(false);
          setExpiryTime(null);
          setTimeRemaining(0);
          
          // Show success message
          toast.success('2FA has been successfully set up!');
        } catch (error) {
          console.error('Error refreshing 2FA methods after enrollment:', error);
          toast.error('2FA was set up, but there was an error refreshing the list of methods');
        }
      };

      await handle2FAEnrolled();

      // Refresh user data to show the new 2FA method
      await refreshUser();

      // Reset form and clear state
      setShowVerificationCode(false);
      setPhoneNumber("");
      setVerificationCode("");
      // Reset expiry timer
      setExpiryTime(null);
      setTimeRemaining(0);
      
      // Success message
      toast.success("2FA enrolled successfully");
      
    } catch (error: any) {
      console.error("Error verifying code:", error);
      
      if (error.code === 'auth/requires-recent-login') {
        setPendingAction(async () => {
          await handleVerifyCode();
          return true;
        });
        setShowPasswordPrompt(true);
      } else if (error.code === 'auth/multi-factor-auth-required') {
        // Handle MFA verification required
        window.mfaResolver = error.resolver;
        setShowMfaVerification(true);
      } else if (error.code === 'auth/code-expired' || 
                 error.message?.includes('expired') || 
                 (error.message && error.message.toLowerCase().includes('session expired'))) {
        // Handle expired verification code or session
        // DED-1520: Don't auto-send new code, just show error and let user click "Request new code" button
        toast.error("Verification code has expired. Please request a new code.");
        
        // Reset timer to show the "Request new code" button
        setExpiryTime(new Date()); // Set to now so timeRemaining becomes 0
        setTimeRemaining(0);
        
        // Reset the verification code input
        setVerificationCode("");
        
        // Clear the stored verification ID to avoid reusing an expired one
        if (window.verificationId) {
          delete window.verificationId;
        }
      } else {
        toast.error(error.message || "Failed to verify the code");
      }
    } finally {
      setIsSubmittingCode(false);
    }
  };

  const handleUnenroll2FA = (factor: TwoFactorMethod) => {
    setSelectedFactorUid(factor.uid);
    setShowUnenrollDialog(true);
  };

  const confirmUnenroll2FA = async () => {
    if (!selectedFactorUid) return;

    // Set up the 2FA unenroll action
    const unenroll2FAAction = async (verificationCode?: string, verificationId?: string) => {
      try {
        console.log(`Attempting to unenroll 2FA factor: ${selectedFactorUid}`);
        if (verificationCode && verificationId) {
          console.log(`Using verification code and ID: ${verificationId.substring(0, 5)}... for unenrollment`);
        }
        
        // Call unenroll with optional verification code and ID if available
        await unenroll2FA(selectedFactorUid, verificationCode, verificationId);
        await refreshUser();

        toast.success("2FA method removed successfully");
        setShowPasswordPrompt(false);
        
        // Clear any verification data
        if (window.verificationId) {
          delete window.verificationId;
        }
        
        return true; // Success
      } catch (error: any) {
        console.error("Error unenrolling 2FA:", error);

        // Handle MFA verification required
        if (error.code === 'auth/multi-factor-auth-required') {
          // Store the resolver for later use
          window.mfaResolver = error.resolver;
          setShowMfaVerification(true);
          // Return false to indicate we're handling this asynchronously
          return false;
        }
        // Handle recent login required
        else if (error.code === 'auth/requires-recent-login' || error.message === 'recent-login-required') {
          setShowPasswordPrompt(true);
          setPendingAction(() => unenroll2FAAction);
          // Return false to indicate we're handling this asynchronously
          return false;
        }
        // Handle expired verification code
        else if (error.code === 'auth/code-expired' || error.message?.includes('expired')) {
          toast.error("Verification code has expired. Please try again.");
          // Clear any verification data
          if (window.verificationId) {
            delete window.verificationId;
          }
          return false;
        }
        // For other errors, re-throw to be handled by the dialog
        throw error;
      } finally {
        // Only clear the dialog if we're not showing MFA verification or password prompt
        if (!showMfaVerification && !showPasswordPrompt) {
          setShowUnenrollDialog(false);
          setSelectedFactorUid(null);
        }
      }
    };

    try {
      // Start the unenroll process
      const success = await unenroll2FAAction();

      // If the action completed successfully or we're handling it asynchronously,
      // we don't need to do anything else here
      if (success === false) {
        return;
      }

      // If we get here, the unenroll was successful
      setShowUnenrollDialog(false);
      setSelectedFactorUid(null);
    } catch (error: any) {
      // Handle any errors that weren't caught by the inner try/catch
      console.error('Error in confirmUnenroll2FA:', error);
      toast.error(error.message || 'Failed to remove 2FA method');
      setShowUnenrollDialog(false);
      setSelectedFactorUid(null);
    }
  };

  const cancelUnenroll2FA = () => {
    setShowUnenrollDialog(false);
    setSelectedFactorUid(null);
    setPendingAction(null);
  };

  const sendMfaVerification = async (factor: TwoFactorMethod, password?: string) => {
    if (!factor) return;

    setIsSendingCode(true);

    try {
      console.log(`Attempting to send verification code to factor: ${factor.phoneNumber}`);
      
      // Clear any existing verification ID to avoid stale data
      if (window.verificationId) {
        console.log('Clearing previous verification ID');
        delete window.verificationId;
      }
      
      // Store the selected factor
      setSelectedFactor(factor);

      // If password is provided, re-authenticate first
      if (password) {
        console.log('Password provided, reauthenticating user');
        const user = getAuth().currentUser;
        if (!user?.email) throw new Error('User not found');

        const credential = EmailAuthProvider.credential(user.email, password);
        await reauthenticateWithCredential(user, credential);
      }

      // Initialize reCAPTCHA verifier if needed
      if (!window.recaptchaVerifier) {
        console.log('Initializing new reCAPTCHA verifier');
        try {
          let container = document.getElementById('recaptcha-container');
          if (!container) {
            container = document.createElement('div');
            container.id = 'recaptcha-container';
            container.style.display = 'none';
            document.body.appendChild(container);
          } else {
            container.innerHTML = '';
          }
          
          const auth = getAuth();
          const verifier = new RecaptchaVerifier(
            auth,
            'recaptcha-container',
            {
              size: 'invisible',
              callback: (response: string) => {
                console.log('reCAPTCHA verified for MFA code sending');
              },
              'expired-callback': () => {
                console.warn('reCAPTCHA expired');
              }
            }
          ) as unknown as { render: () => Promise<void> } & ApplicationVerifier;
          
          window.recaptchaVerifier = verifier as unknown as ApplicationVerifier;
          await verifier.render();
        } catch (error) {
          console.error('Error initializing reCAPTCHA:', error);
          throw new Error('Failed to initialize security verification');
        }
      }

      console.log(`Sending verification code to: ${factor.phoneNumber}`);
      // Send verification code to the selected factor
      const verificationId = await verifyPhoneNumber(factor.phoneNumber);

      if (verificationId) {
        console.log(`Received verification ID: ${verificationId.substring(0, 5)}...`);
        window.verificationId = verificationId;
        setVerificationSent(true);
        toast.success(`Verification code sent to ${factor.phoneNumber}`);
      } else {
        throw new Error('Failed to send verification code');
      }
    } catch (error: any) {
      console.error('Error in sendMfaVerification:', error);

      if (error.code === 'auth/requires-recent-login') {
        // Store the current action to retry after password verification
        const retryAction = async () => {
          await sendMfaVerification(factor);
        };

        // Show password prompt
        setPendingAction(() => retryAction);
        setShowPasswordPrompt(true);
      } else {
        toast.error(error.message || 'Failed to send verification code');
      }
    } finally {
      setIsSendingCode(false);
    }
  };

  const handlePasswordSubmit = async () => {
    if (!currentPassword) {
      toast.error('Please enter your password');
      return;
    }

    setIsVerifyingPassword(true);

    try {
      // Re-authenticate the user
      const user = getAuth().currentUser;
      if (!user?.email) throw new Error('User not found');

      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);

      // If we have a pending action, execute it
      if (pendingAction) {
        try {
          await pendingAction();
          // If we get here, the action completed successfully without MFA
          setShowPasswordPrompt(false);
          setCurrentPassword('');
          setPendingAction(null);
          toast.success('Verification successful');
        } catch (error: any) {
          // Check if MFA verification is required
          if (error.code === 'auth/multi-factor-auth-required') {
            // Store the resolver for later use
            window.mfaResolver = error.resolver;
            // Show MFA verification dialog
            setShowMfaVerification(true);
            // Don't close the password dialog yet
            return;
          }
          // Re-throw other errors to be caught by the outer catch
          throw error;
        }
      } else {
        // No pending action, just close the dialog
        setShowPasswordPrompt(false);
        setCurrentPassword('');
        toast.success('Verification successful');
      }
    } catch (error: any) {
      console.error('Error during password verification:', error);

      if (error.code === 'auth/wrong-password') {
        toast.error('Incorrect password. Please try again.');
      } else if (error.code === 'auth/too-many-requests') {
        toast.error('Too many failed attempts. Please try again later.');
      } else if (error.code === 'auth/multi-factor-auth-required') {
        // Store the resolver for later use
        window.mfaResolver = error.resolver;
        // Show MFA verification dialog first to let user select a factor
        setShowMfaVerification(true);
        // Don't close the password dialog yet
        return;
      } else {
        // Don't show error for MFA required, we handle that above
        toast.error(error.message || 'Failed to verify password');
      }
    } finally {
      setIsVerifyingPassword(false);
    }
  };

  const handleMfaVerification = async () => {
    if (mfaCode.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }

    console.log('Verifying MFA code:', mfaCode);
    console.log('Using verification ID:', window.verificationId ? `${window.verificationId.substring(0, 5)}...` : 'Not available');
    
    setIsVerifyingMfa(true);
    try {
      // Handle unenrollment with MFA verification
      if (pendingUnenrollFactor) {
        if (!window.verificationId) {
          throw new Error('Verification ID is missing. Please request a new code.');
        }
        
        console.log(`Attempting to unenroll 2FA factor ${pendingUnenrollFactor.uid} with verification code`);
        await unenroll2FA(
          pendingUnenrollFactor.uid,
          mfaCode,
          window.verificationId
        );

        // Refresh the list of enrolled factors
        const factors = await getEnrolledFactors();
        setEnrolledMethods(factors.map(mapToTwoFactorMethod));

        // Clear verification data
        delete window.verificationId;
        
        // Show success message and reset state
        toast.success('2FA method removed successfully');
        setShowMfaVerification(false);
        setMfaCode('');
        setPendingUnenrollFactor(null);
        
        // Reset the selected factor UID and close the dialog
        setSelectedFactorUid(null);
        setShowUnenrollDialog(false);
      }
      // Handle other pending actions that require MFA verification
      else if (pendingAction) {
        // Execute the pending action with MFA code if needed
        try {
          if (typeof pendingAction === 'function') {
            // Call the pending action with verification information
            if (window.verificationId) {
              console.log('Executing pending action with verification info');
              // The pending action might need the verification code and ID
              await pendingAction(mfaCode, window.verificationId);
            } else {
              console.log('Executing pending action without verification info');
              await pendingAction();
            }
          } else {
            await pendingAction;
          }
          
          // Clear verification data after successful action
          if (window.verificationId) {
            delete window.verificationId;
          }
        } catch (error: any) {
          console.error('Error during MFA verification:', error);
          
          if (error.code === 'auth/code-expired' || error.message?.includes('expired')) {
            toast.error('Verification code has expired. Please request a new code.');
            // Clear the expired verification ID
            if (window.verificationId) {
              delete window.verificationId;
            }
            setVerificationSent(false);
          } else {
            toast.error(error.message || 'Failed to verify MFA code');
          }
          
          setMfaCode('');
          return;
        }
        // If we get here, the action was successful
        toast.success('Verification successful');
        setShowMfaVerification(false);
        setMfaCode('');
        setPendingAction(null);
      }
    } catch (error: any) {
      console.error('Error during MFA verification:', error);

      // Handle specific error cases
      if (error.code === 'auth/invalid-verification-code') {
        toast.error('Invalid verification code. Please try again.');
      } else if (error.code === 'auth/code-expired') {
        toast.error('Verification code has expired. Please request a new one.');
      } else {
        toast.error(error.message || 'Failed to verify MFA code');
      }
    } finally {
      setIsVerifyingMfa(false);
    }
  };

  const handleLinkProvider = async (providerType: 'google' | 'microsoft') => {
    setIsLinkingProvider(providerType);
    try {
      const currentUser = getAuth().currentUser;
      if (!currentUser) {
        throw new Error('No authenticated user found');
      }

      let provider;
      if (providerType === 'google') {
        provider = new GoogleAuthProvider();
        provider.setCustomParameters({
          prompt: 'select_account'
        });
      } else {
        provider = new OAuthProvider('microsoft.com');
        provider.setCustomParameters({
          prompt: 'select_account'
        });
      }

      await linkWithPopup(currentUser, provider);
      
      // Update linked providers
      const providers = currentUser.providerData
        .map((p: UserInfo) => p.providerId)
        .filter((id: string) => id === 'google.com' || id === 'microsoft.com');
      setLinkedProviders(providers);
      
      toast.success(`${providerType === 'google' ? 'Google' : 'Microsoft'} account linked successfully`);
    } catch (error: any) {
      console.error('Error linking provider:', error);
      
      if (error.code === 'auth/credential-already-in-use') {
        toast.error('This account is already linked to another user');
      } else if (error.code === 'auth/provider-already-linked') {
        toast.error('This provider is already linked to your account');
      } else if (error.code === 'auth/popup-closed-by-user') {
        toast.info('Sign-in popup was closed');
      } else if (error.code === 'auth/requires-recent-login') {
        setPendingAction(async () => {
          await handleLinkProvider(providerType);
        });
        setShowPasswordPrompt(true);
      } else {
        toast.error(error.message || `Failed to link ${providerType === 'google' ? 'Google' : 'Microsoft'} account`);
      }
    } finally {
      setIsLinkingProvider(null);
    }
  };

  const handleUnlinkProvider = async (providerId: string) => {
    setIsUnlinkingProvider(providerId);
    try {
      const currentUser = getAuth().currentUser;
      if (!currentUser) {
        throw new Error('No authenticated user found');
      }

      // Check if user has at least one other sign-in method
      const hasPassword = currentUser.providerData.some((p: UserInfo) => p.providerId === 'password');
      const linkedProviderCount = currentUser.providerData.filter(
        (p: UserInfo) => p.providerId === 'google.com' || p.providerId === 'microsoft.com'
      ).length;

      if (!hasPassword && linkedProviderCount <= 1) {
        toast.error('Cannot unlink the only sign-in method. Please set a password or link another provider first.');
        return;
      }

      await unlink(currentUser, providerId);
      
      // Update linked providers
      const providers = currentUser.providerData
        .map((p: UserInfo) => p.providerId)
        .filter((id: string) => id === 'google.com' || id === 'microsoft.com');
      setLinkedProviders(providers);
      
      const providerName = providerId === 'google.com' ? 'Google' : 'Microsoft';
      toast.success(`${providerName} account unlinked successfully`);
    } catch (error: any) {
      console.error('Error unlinking provider:', error);
      
      if (error.code === 'auth/requires-recent-login') {
        setPendingAction(async () => {
          await handleUnlinkProvider(providerId);
        });
        setShowPasswordPrompt(true);
      } else {
        toast.error(error.message || 'Failed to unlink provider');
      }
    } finally {
      setIsUnlinkingProvider(null);
    }
  };

  const currentUserUID = getAuthUID();
  const ALLOWED_SSO_UIDS = ['qflhJVvKDDQZVNgWZaVkR9LKG8b2', 'atQgi2ZUAzOv2buzTayUeXvlqeu2'];
  
  const TAB_GROUPS = [
    {
      group: "Password",
      options: [
        {
          value: "password",
          label: "Change Password",
          description: "Change your password",
        }
      ]
    },
    {
      group: "2FA",
      options: [
        {
          value: "2fa",
          label: "Manage 2FA",
          description: "Manage your 2FA",
        }
      ]
    },
    {
      group: "SSO",
      options: [
        {
          value: "sso",
          label: "Single Sign-On",
          description: "Link Microsoft or Google",
        }
      ]
    }
  ];

  return (
    <div className="space-y-6">
      {/* Password Verification Dialog */}
      <Dialog open={showPasswordPrompt} onOpenChange={(open) => {
        if (!open) {
          setShowPasswordPrompt(false);
          setCurrentPassword('');
          setPendingAction(null);
        }
      }}>
        <DialogContent className="sm:max-w-[425px] bg-card">
          <DialogHeader>
            <DialogTitle>Verify Your Identity</DialogTitle>
            <DialogDescription>
              For security, please enter your password to continue.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Enter your password"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handlePasswordSubmit();
                  }
                }}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowPasswordPrompt(false);
                setCurrentPassword('');
                setPendingAction(null);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handlePasswordSubmit}
              disabled={isVerifyingPassword || !currentPassword}
            >
              {isVerifyingPassword ? 'Verifying...' : 'Continue'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unenroll 2FA Confirmation Dialog */}
      <AlertDialog open={showUnenrollDialog} onOpenChange={setShowUnenrollDialog}>
        <AlertDialogContent className="bg-secondary">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove 2FA Method</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove this 2FA method? You'll need to set it up again if you want to use it in the future.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={cancelUnenroll2FA}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmUnenroll2FA}
              className="bg-destructive text-white hover:bg-destructive/90"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* MFA Verification Dialog */}
      <Dialog open={showMfaVerification} onOpenChange={(open) => {
        if (!open) {
          setShowMfaVerification(false);
          setMfaCode('');
          setVerificationSent(false);
          setSelectedFactor(null);
        }
      }}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Verify Your Identity</DialogTitle>
            <DialogDescription>
              {verificationSent
                ? `Enter the 6-digit verification code sent to ${selectedFactor?.phoneNumber}`
                : 'Select a 2FA method to verify your identity'}
            </DialogDescription>
          </DialogHeader>

          {!verificationSent ? (
            <div className="space-y-4 py-4">
              <h4 className="text-sm font-medium">Available 2FA Methods</h4>
              <div className="space-y-2">
                {enrolledMethods.length > 0 ? (
                  enrolledMethods.map((factor) => (
                    <div key={factor.uid} className="flex items-center justify-between p-3 border rounded-md">
                      <div>
                        <p className="font-medium">{factor.displayName || 'Phone'}</p>
                        <p className="text-sm text-muted-foreground">{factor.phoneNumber}</p>
                      </div>
                      <Button
                        onClick={() => sendMfaVerification(factor)}
                        disabled={isSendingCode}
                        size="sm"
                      >
                        {isSendingCode && selectedFactor?.uid === factor.uid ? 'Sending...' : 'Verify'}
                      </Button>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-muted-foreground">No 2FA methods enrolled</p>
                )}
              </div>
            </div>
          ) : (
            <div className="space-y-4 py-4">
              <div className="flex flex-col items-center justify-center space-y-4">
                <InputOTP
                  maxLength={6}
                  value={mfaCode}
                  onChange={(value) => setMfaCode(value)}
                  onComplete={handleMfaVerification}
                  autoFocus
                >
                  <InputOTPGroup>
                    {[...Array(6)].map((_, i) => (
                      <InputOTPSlot key={i} index={i} />
                    ))}
                  </InputOTPGroup>
                </InputOTP>
                <Button
                  variant="link"
                  size="sm"
                  className="h-auto p-0 text-sm"
                  onClick={() => sendMfaVerification(selectedFactor!)}
                  disabled={isSendingCode}
                >
                  {isSendingCode ? 'Sending...' : 'Resend code'}
                </Button>
              </div>

              <DialogFooter className="mt-4">
                <Button
                  variant="outline"
                  onClick={() => {
                    setVerificationSent(false);
                    setMfaCode('');
                  }}
                  disabled={isVerifyingMfa}
                >
                  Back
                </Button>
                <Button
                  onClick={handleMfaVerification}
                  disabled={isVerifyingMfa || mfaCode.length !== 6}
                  className="ml-2"
                >
                  {isVerifyingMfa ? 'Verifying...' : 'Verify'}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
      <div className="bg-card rounded-xl border border-border shadow-sm px-6 py-6">
        {/* <h2 className="text-xl font-semibold mb-4 text-foreground">Security Settings</h2> */}
        <BeautifulTabsSelector
          options={TAB_GROUPS}
          value={selectedTab}
          onChange={setSelectedTab}
          selectorLabel="Security"
        />
        <p className="text-sm text-muted-foreground mb-6">
          Update your security settings, including password and two-factor authentication.
        </p>

        <Tabs defaultValue="password" value={selectedTab} className="w-full">

          {/* Password Tab Content */}
          <TabsContent value="password" className="space-y-4">
            <form onSubmit={handlePasswordUpdate} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password">New Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter new password"
                  required
                  minLength={8}
                />
                <p className="text-xs text-muted-foreground">
                  Password must be a minimum of 8 characters, include numbers and special characters.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm New Password</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  required
                />
              </div>

              <div className="pt-2">
                <Button type="submit" disabled={isUpdatingPassword}>
                  {isUpdatingPassword ? "Updating..." : "Update Password"}
                </Button>
              </div>
            </form>
          </TabsContent>

          {/* 2FA Tab Content */}
          <TabsContent value="2fa" className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-foreground mb-2">Enrol 2FA</h3>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="phoneNumber">Phone Number</Label>
                  <div className="flex gap-2">
                    <Select
                      value={countryCode}
                      onValueChange={setCountryCode}
                    >
                      <SelectTrigger className="w-[180px]">
                        <SelectValue placeholder="Select country" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="+61">+61 (Australia)</SelectItem>
                        <SelectItem value="+44">+44 (UK)</SelectItem>
                        <SelectItem value="+1">+1 (USA/Canada)</SelectItem>
                        <SelectItem value="+64">+64 (New Zealand)</SelectItem>
                        <SelectItem value="+49">+49 (Germany)</SelectItem>
                        <SelectItem value="+52">+52 (Mexico)</SelectItem>
                        <SelectItem value="+81">+81 (Japan)</SelectItem>
                        <SelectItem value="+86">+86 (China)</SelectItem>
                        <SelectItem value="+91">+91 (India)</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input
                      type="tel"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, ""))}
                      placeholder="Enter phone number"
                      className="flex-1"
                      maxLength={15}
                    />
                    <Button
                      type="button"
                      onClick={handleVerifyPhoneNumber}
                      disabled={isVerifying || !phoneNumber}
                    >
                      {isVerifying ? "Sending..." : "Verify"}
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    We'll send a verification code to your phone number.
                  </p>

                  {showVerificationCode && (
                    <div className="mt-4 space-y-2">
                      <div className="flex justify-between items-center">
                        <Label htmlFor="verificationCode">Verification Code</Label>
                        {timeRemaining > 0 && (
                          <div className={`text-xs font-medium ${timeRemaining <= 30 ? 'text-destructive' : 'text-muted-foreground'}`}>
                            Code expires in: {Math.floor(timeRemaining / 60)}:{String(timeRemaining % 60).padStart(2, '0')}
                          </div>
                        )}
                        {timeRemaining === 0 && expiryTime && (
                          <div className="text-xs font-medium text-destructive">
                            Code expired
                          </div>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Input
                          id="verificationCode"
                          type="text"
                          value={verificationCode}
                          onChange={(e) => setVerificationCode(e.target.value)}
                          placeholder="Enter 6-digit code"
                          className="max-w-[200px]"
                        />
                        <Button
                          type="button"
                          onClick={handleVerifyCode}
                          disabled={isSubmittingCode || !verificationCode || (timeRemaining === 0 && expiryTime !== null)}
                        >
                          {isSubmittingCode ? "Verifying..." : "Verify Code"}
                        </Button>
                      </div>
                      {timeRemaining === 0 && expiryTime && (
                        <Button 
                          variant="outline" 
                          size="sm" 
                          type="button" 
                          onClick={refreshVerification}
                          disabled={isVerifying}
                          className="mt-2"
                        >
                          {isVerifying ? "Sending..." : "Request new code"}
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {enrolledMethods.length > 0 && (
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-foreground">Enrolled 2FA Methods</h3>
                <div className="space-y-2">
                  {enrolledMethods.map((method) => (
                    <div key={method.uid} className="flex items-center justify-between p-3 border rounded-lg">
                      <div>
                        <p className="font-medium">{method.displayName}</p>
                        <p className="text-sm text-muted-foreground">{method.phoneNumber}</p>
                        <p className="text-xs text-muted-foreground">Enrolled: {method.enrollmentTime}</p>
                      </div>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleUnenroll2FA(method)}
                        disabled={isVerifyingMfa}
                      >
                        Remove
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </TabsContent>

          {/* SSO Tab Content */}
          <TabsContent value="sso" className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-foreground mb-2">Single Sign-On</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Link your account with Google or Microsoft to enable single sign-on. You can use these providers to sign in instead of your password.
              </p>
              
              <div className="space-y-4">
                {/* Google SSO */}
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center border">
                      <svg className="w-5 h-5" viewBox="0 0 24 24">
                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                      </svg>
                    </div>
                    <div>
                      <p className="font-medium text-foreground">Google</p>
                      <p className="text-sm text-muted-foreground">
                        {linkedProviders.includes('google.com') ? 'Linked' : 'Not linked'}
                      </p>
                    </div>
                  </div>
                  {linkedProviders.includes('google.com') ? (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleUnlinkProvider('google.com')}
                      disabled={isUnlinkingProvider === 'google.com'}
                    >
                      {isUnlinkingProvider === 'google.com' ? 'Unlinking...' : 'Unlink'}
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      onClick={() => handleLinkProvider('google')}
                      disabled={isLinkingProvider === 'google'}
                    >
                      {isLinkingProvider === 'google' ? 'Linking...' : 'Link Account'}
                    </Button>
                  )}
                </div>

                {/* Microsoft SSO */}
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center border">
                      <svg className="w-5 h-5" viewBox="0 0 24 24">
                        <path fill="#f35325" d="M1 1h10v10H1z"/>
                        <path fill="#81bc06" d="M13 1h10v10H13z"/>
                        <path fill="#05a6f0" d="M1 13h10v10H1z"/>
                        <path fill="#ffba08" d="M13 13h10v10H13z"/>
                      </svg>
                    </div>
                    <div>
                      <p className="font-medium text-foreground">Microsoft</p>
                      <p className="text-sm text-muted-foreground">
                        {linkedProviders.includes('microsoft.com') ? 'Linked' : 'Not linked'}
                      </p>
                    </div>
                  </div>
                  {linkedProviders.includes('microsoft.com') ? (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleUnlinkProvider('microsoft.com')}
                      disabled={isUnlinkingProvider === 'microsoft.com'}
                    >
                      {isUnlinkingProvider === 'microsoft.com' ? 'Unlinking...' : 'Unlink'}
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      onClick={() => handleLinkProvider('microsoft')}
                      disabled={isLinkingProvider === 'microsoft'}
                    >
                      {isLinkingProvider === 'microsoft' ? 'Linking...' : 'Link Account'}
                    </Button>
                  )}
                </div>
              </div>

              <div className="mt-6 p-4 bg-muted/50 rounded-lg">
                <h4 className="text-sm font-medium text-foreground mb-2">About Single Sign-On</h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Link your Google or Microsoft account to sign in without a password</li>
                  <li>• You can link multiple providers for more sign-in options</li>
                  <li>• You must have at least one sign-in method (password or linked provider)</li>
                  <li>• Unlinking a provider will not delete your account</li>
                </ul>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
